import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import DoneAllIcon from '@mui/icons-material/DoneAll';

import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import type { TFunction } from 'i18next';

import AppTabsBase from '../../components/designe/app-tabs/AppTabsBase';
import PageContainer from '../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../components/layout/page-container/page-container-error-handler';
import { usePullRefreshInvalidateTags } from '../../hooks/usePullRefreshInvalidateTags';
import { useGetCommunityByIdLazy } from '../../redux/api/communities/communitiesApi';
import {
  IUserNotificationItem,
  NOTIFICATIONS_TAG,
  NotificationDisplayDomain,
  UNREAD_COUNT_TAG,
  UNREAD_COUNT_TAG_ID,
  notificationsApi,
  useGetUnreadCountQuery,
  useLazyGetNotificationsQuery,
  useLazyGetUnreadNotificationsQuery,
  useMarkNotificationsReadMutation,
} from '../../redux/api/notificationsApi';
import { useGetMyDetails } from '../../redux/api/users/usersApi';
import { useLazyGetUserDetails } from '../../redux/api/users/usersApi';
import {
  NOTIFICATION_TEMPLATE_POST_COMMENTED,
  NOTIFICATION_TEMPLATE_PROGRAM_ITEM_SHARED_WITH_USER,
  NOTIFICATION_TEMPLATE_TRAINING_CREATED_FOR_OWNER,
  NOTIFICATION_TEMPLATE_TRAINING_SHARED_WITH_USER,
  NOTIFICATION_TEMPLATE_USER_SUBSCRIPTION_APPROVED,
  NOTIFICATION_TEMPLATE_WALL_POST_ON_USER_PAGE,
} from '../../shared/notification-template-codes';
import { Paths } from '../../shared/paths';
import { getDateFnsLocale } from '../../utils/datetime/date-time-utils';
import { getNotificationDomainIcon } from '../../utils/notification-display';
import { resolveNotificationNavigationPath } from '../../utils/notification-navigation';
import { useStyles } from './Notifications.styles';

const PAGE_SIZE = 20;
const INITIAL_SKELETON_COUNT = 4;
const APPEND_SKELETON_COUNT = 2;
const INLINE_MARKER_REGEX = /\(@([^)]+)\)/g;

type INotificationTextSegment = { text: string; to: string } | string;

function getRecordData(item: IUserNotificationItem): Record<string, unknown> {
  if (!item.data || typeof item.data !== 'object') {
    return {};
  }
  return item.data as Record<string, unknown>;
}

function getRecordString(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key];
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function resolveWallOwnerPath(params: {
  ownerId: string;
  ownerType?: string;
  currentUserId?: string;
}): string {
  const { ownerId, ownerType, currentUserId } = params;
  if (ownerType === 'community') {
    return `${Paths.communities.root}/${ownerId}`;
  }
  if (currentUserId && ownerId === currentUserId) {
    return Paths.users.me;
  }
  return Paths.users.toUser(ownerId);
}

function appendEntityDeepLink(
  basePath: string,
  payload: Record<string, unknown>,
): string {
  const postId = getRecordString(payload, 'postId');
  const commentId = getRecordString(payload, 'commentId');
  if (!postId && !commentId) {
    return basePath;
  }
  const searchParams = new URLSearchParams();
  if (postId) {
    searchParams.set('postId', postId);
  }
  if (commentId) {
    searchParams.set('commentId', commentId);
  }
  const search = searchParams.toString();
  return search ? `${basePath}?${search}` : basePath;
}

function resolveMarkerLink(params: {
  marker: string;
  item: IUserNotificationItem;
  currentUserId?: string;
  t: TFunction;
  resolvedUserNames: Record<string, string>;
  resolvedCommunityNames: Record<string, string>;
}): { text: string; to: string } | null {
  const {
    marker,
    item,
    currentUserId,
    t,
    resolvedUserNames,
    resolvedCommunityNames,
  } = params;
  const record = getRecordData(item);
  const templateCode = item.templateCode ?? '';
  const wallOwnerId = getRecordString(record, 'wallOwnerId');
  const wallOwnerType = getRecordString(record, 'wallOwnerType')?.toLowerCase();
  const subscriberUserId = getRecordString(record, 'subscriberUserId');
  const trainingId = getRecordString(record, 'trainingId');
  const trainingTitle = getRecordString(record, 'trainingTitle');
  const programItemId = getRecordString(record, 'programItemId');
  const programTitle = getRecordString(record, 'programTitle');
  const postId = getRecordString(record, 'postId');

  if (
    (templateCode === NOTIFICATION_TEMPLATE_WALL_POST_ON_USER_PAGE ||
      templateCode === NOTIFICATION_TEMPLATE_POST_COMMENTED) &&
    (marker === 'user' || marker === 'community') &&
    wallOwnerId
  ) {
    const wallPath = resolveWallOwnerPath({
      ownerId: wallOwnerId,
      ownerType: marker,
      currentUserId,
    });
    return {
      text: t(`common.${marker}`),
      to: appendEntityDeepLink(wallPath, record),
    };
  }

  if (
    marker === wallOwnerId &&
    wallOwnerId &&
    (templateCode === NOTIFICATION_TEMPLATE_WALL_POST_ON_USER_PAGE ||
      templateCode === NOTIFICATION_TEMPLATE_POST_COMMENTED)
  ) {
    const isCommunityOwner = wallOwnerType === 'community';
    const resolvedOwnerName =
      wallOwnerId && isCommunityOwner
        ? resolvedCommunityNames[wallOwnerId]
        : wallOwnerId
          ? resolvedUserNames[wallOwnerId]
          : undefined;
    const ownerLabel =
      resolvedOwnerName ??
      (isCommunityOwner
        ? t('common.community')
        : currentUserId && wallOwnerId === currentUserId
          ? t('common.myProfile')
          : t('common.user'));
    const wallPath = resolveWallOwnerPath({
      ownerId: wallOwnerId,
      ownerType: wallOwnerType,
      currentUserId,
    });
    return {
      text: ownerLabel,
      to: appendEntityDeepLink(wallPath, record),
    };
  }

  if (
    marker === subscriberUserId &&
    subscriberUserId &&
    templateCode === NOTIFICATION_TEMPLATE_USER_SUBSCRIPTION_APPROVED
  ) {
    return {
      text: resolvedUserNames[subscriberUserId] ?? t('common.user'),
      to: Paths.users.toUser(subscriberUserId),
    };
  }

  if (
    marker === trainingId &&
    trainingId &&
    (templateCode === NOTIFICATION_TEMPLATE_TRAINING_SHARED_WITH_USER ||
      templateCode === NOTIFICATION_TEMPLATE_TRAINING_CREATED_FOR_OWNER)
  ) {
    return {
      text: trainingTitle ?? t('common.training'),
      to: Paths.trainings.toViewId(trainingId),
    };
  }

  if (
    marker === programItemId &&
    programItemId &&
    templateCode === NOTIFICATION_TEMPLATE_PROGRAM_ITEM_SHARED_WITH_USER
  ) {
    return {
      text: programTitle ?? t('common.title'),
      to: Paths.programs.toViewItemId(programItemId),
    };
  }

  if (
    marker === postId &&
    templateCode === NOTIFICATION_TEMPLATE_WALL_POST_ON_USER_PAGE
  ) {
    return {
      text: '',
      to: '',
    };
  }

  return null;
}

function linkifyNotificationText(params: {
  text: string;
  item: IUserNotificationItem;
  currentUserId?: string;
  t: TFunction;
  resolvedUserNames: Record<string, string>;
  resolvedCommunityNames: Record<string, string>;
}): INotificationTextSegment[] {
  const {
    text,
    item,
    currentUserId,
    t,
    resolvedUserNames,
    resolvedCommunityNames,
  } = params;
  if (!text.includes('(@')) {
    return [text];
  }

  const segments: INotificationTextSegment[] = [];
  let cursor = 0;

  for (const match of text.matchAll(INLINE_MARKER_REGEX)) {
    const fullMatch = match[0];
    const marker = match[1]?.trim();
    if (!fullMatch || !marker || typeof match.index !== 'number') {
      continue;
    }

    if (match.index > cursor) {
      segments.push(text.slice(cursor, match.index));
    }

    const linked = resolveMarkerLink({
      marker,
      item,
      currentUserId,
      t,
      resolvedUserNames,
      resolvedCommunityNames,
    });
    if (linked) {
      if (!linked.text) {
        cursor = match.index + fullMatch.length;
        continue;
      }
      segments.push(linked);
    } else {
      segments.push(fullMatch);
    }

    cursor = match.index + fullMatch.length;
  }

  if (cursor < text.length) {
    segments.push(text.slice(cursor));
  }

  return segments.length > 0 ? segments : [text];
}

interface INotificationRowProps {
  item: IUserNotificationItem;
  currentUserId?: string;
  resolvedUserNames: Record<string, string>;
  resolvedCommunityNames: Record<string, string>;
  onVisibleUnread: (id: string) => void;
}

const NotificationRow = (props: INotificationRowProps) => {
  const {
    item,
    currentUserId,
    resolvedUserNames,
    resolvedCommunityNames,
    onVisibleUnread,
  } = props;
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { classes } = useStyles(theme);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const isUnread = !item.readAt;

  const navigatePath = resolveNotificationNavigationPath(item, {
    currentUserId,
  });
  const IconComponent = getNotificationDomainIcon(
    item.displayDomain as NotificationDisplayDomain,
  );

  const titleText = item.title ?? '';
  const secondaryText = item.body && item.body !== item.title ? item.body : '';
  const secondaryTextSegments = secondaryText
    ? linkifyNotificationText({
        text: secondaryText,
        item,
        currentUserId,
        t,
        resolvedUserNames,
        resolvedCommunityNames,
      })
    : [];

  useEffect(() => {
    if (!isUnread || !rootRef.current) {
      return undefined;
    }
    const el = rootRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onVisibleUnread(item.id);
        }
      },
      { root: null, threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isUnread, item.id, onVisibleUnread]);

  const handleActivate = useCallback(() => {
    if (navigatePath) {
      navigate(navigatePath);
    }
  }, [navigate, navigatePath]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!navigatePath) {
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleActivate();
      }
    },
    [handleActivate, navigatePath],
  );

  let dateLabel = '';
  try {
    const created = parseISO(item.createdAt);
    const locale = getDateFnsLocale(i18n.language);
    dateLabel = `${format(created, 'P', { locale })}, ${format(created, 'HH:mm', { locale })}`;
  } catch {
    dateLabel = item.createdAt;
  }

  return (
    <Box
      ref={rootRef}
      className={clsx(classes.card, navigatePath && classes.cardClickable)}
      onClick={navigatePath ? handleActivate : undefined}
      onKeyDown={navigatePath ? handleKeyDown : undefined}
      role={navigatePath ? 'button' : undefined}
      tabIndex={navigatePath ? 0 : undefined}>
      {isUnread ? <Box className={classes.accentBar} /> : null}
      <Box className={classes.cardInner}>
        <Box className={classes.rowIconWrap}>
          <IconComponent className={classes.rowIcon} fontSize="small" />
        </Box>
        <Box className={classes.rowText}>
          <Typography
            variant="text-md-sb"
            className={clsx(
              classes.rowLine,
              isUnread ? classes.rowTitle : classes.rowTitleRead,
            )}>
            {titleText || '—'}
          </Typography>
          {secondaryText ? (
            <Typography
              variant="text-sm"
              lineHeight={'1.15rem'}
              className={clsx(classes.rowLine, classes.rowSecondary)}>
              {secondaryTextSegments.map((segment, index) => {
                if (typeof segment === 'string') {
                  return (
                    <React.Fragment key={`seg-t-${item.id}-${index}`}>
                      {segment}
                    </React.Fragment>
                  );
                }
                return (
                  <Box
                    key={`seg-l-${item.id}-${index}`}
                    component="span"
                    role="link"
                    tabIndex={0}
                    sx={{
                      color: 'text.secondary',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'text.secondary',
                        outlineOffset: '2px',
                        borderRadius: '2px',
                      },
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      navigate(segment.to);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        navigate(segment.to);
                      }
                    }}>
                    {segment.text}
                  </Box>
                );
              })}
            </Typography>
          ) : null}
          <Typography
            variant="label-text-sm"
            className={clsx(classes.rowLine, classes.rowMeta)}>
            {dateLabel}
          </Typography>
        </Box>
        {isUnread ? <Box className={classes.unreadDot} /> : null}
      </Box>
    </Box>
  );
};

interface INotificationRowSkeletonProps {
  compact?: boolean;
}

const NotificationRowSkeleton = (props: INotificationRowSkeletonProps) => {
  const { compact = false } = props;
  const theme = useTheme();
  const { classes } = useStyles(theme);

  return (
    <Box
      className={classes.card}
      data-testid="notification-row-skeleton"
      aria-hidden>
      <Box
        className={clsx(
          classes.cardInner,
          compact && classes.skeletonCardInnerCompact,
        )}>
        <Box className={classes.rowIconWrap}>
          <Skeleton
            className={classes.skeletonIcon}
            variant="rounded"
            width={24}
            height={24}
          />
        </Box>
        <Box className={classes.rowText}>
          <Skeleton variant="text" width="78%" height={compact ? 20 : 24} />
          {!compact ? (
            <Skeleton variant="text" width="62%" height={20} />
          ) : null}
          <Skeleton variant="text" width="44%" height={compact ? 16 : 18} />
        </Box>
      </Box>
    </Box>
  );
};

const Notifications = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const isReadAllIconOnly = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: me } = useGetMyDetails();
  const { data: unreadCountData } = useGetUnreadCountQuery(undefined, {
    pollingInterval: 60000,
  });
  const unreadCount = unreadCountData?.unreadCount ?? 0;

  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const [items, setItems] = useState<IUserNotificationItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [appendLoading, setAppendLoading] = useState(false);
  const [readAllLoading, setReadAllLoading] = useState(false);
  const [listError, setListError] = useState<unknown | null>(null);

  const [triggerAll] = useLazyGetNotificationsQuery();
  const [triggerUnread] = useLazyGetUnreadNotificationsQuery();
  const [markRead] = useMarkNotificationsReadMutation();
  const [triggerUserDetails] = useLazyGetUserDetails();
  const [triggerCommunityById] = useGetCommunityByIdLazy();
  const requestedUserIdsRef = useRef<Set<string>>(new Set());
  const requestedCommunityIdsRef = useRef<Set<string>>(new Set());
  const [resolvedUserNames, setResolvedUserNames] = useState<
    Record<string, string>
  >({});
  const [resolvedCommunityNames, setResolvedCommunityNames] = useState<
    Record<string, string>
  >({});

  const triggerAllRef = useRef(triggerAll);
  const triggerUnreadRef = useRef(triggerUnread);
  useLayoutEffect(() => {
    triggerAllRef.current = triggerAll;
    triggerUnreadRef.current = triggerUnread;
  }, [triggerAll, triggerUnread]);

  const visibilityBufferRef = useRef<Set<string>>(new Set());
  const itemsRef = useRef<IUserNotificationItem[]>([]);
  const markReadRef = useRef(markRead);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<() => void>(() => {});
  const isMountedRef = useRef(true);
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useLayoutEffect(() => {
    markReadRef.current = markRead;
  }, [markRead]);

  useEffect(() => {
    return () => {
      const ids = [...visibilityBufferRef.current].filter((id) => {
        const n = itemsRef.current.find((x) => x.id === id);
        if (n) {
          return !n.readAt;
        }
        return true;
      });
      visibilityBufferRef.current.clear();
      if (ids.length > 0) {
        void markReadRef.current({ ids });
      }
    };
  }, []);

  useEffect(() => {
    const userIdsToResolve = new Set<string>();
    const communityIdsToResolve = new Set<string>();

    for (const item of items) {
      const payload = getRecordData(item);
      const wallOwnerId = getRecordString(payload, 'wallOwnerId');
      const wallOwnerType = getRecordString(
        payload,
        'wallOwnerType',
      )?.toLowerCase();
      const subscriberUserId = getRecordString(payload, 'subscriberUserId');

      if (
        wallOwnerId &&
        (item.templateCode === NOTIFICATION_TEMPLATE_WALL_POST_ON_USER_PAGE ||
          item.templateCode === NOTIFICATION_TEMPLATE_POST_COMMENTED)
      ) {
        if (wallOwnerType === 'community') {
          if (!resolvedCommunityNames[wallOwnerId]) {
            communityIdsToResolve.add(wallOwnerId);
          }
        } else if (!resolvedUserNames[wallOwnerId]) {
          userIdsToResolve.add(wallOwnerId);
        }
      }

      if (
        subscriberUserId &&
        item.templateCode ===
          NOTIFICATION_TEMPLATE_USER_SUBSCRIPTION_APPROVED &&
        !resolvedUserNames[subscriberUserId]
      ) {
        userIdsToResolve.add(subscriberUserId);
      }
    }

    userIdsToResolve.forEach((id) => {
      if (requestedUserIdsRef.current.has(id)) {
        return;
      }
      requestedUserIdsRef.current.add(id);
      void triggerUserDetails({ id })
        .unwrap()
        .then((user) => {
          const fullName = [user.firstname, user.lastname]
            .map((v) => (typeof v === 'string' ? v.trim() : ''))
            .filter(Boolean)
            .join(' ');
          const name = fullName || t('common.user');
          setResolvedUserNames((prev) => ({ ...prev, [id]: name }));
        })
        .catch(() => {
          setResolvedUserNames((prev) =>
            prev[id] ? prev : { ...prev, [id]: t('common.user') },
          );
        });
    });

    communityIdsToResolve.forEach((id) => {
      if (requestedCommunityIdsRef.current.has(id)) {
        return;
      }
      requestedCommunityIdsRef.current.add(id);
      void triggerCommunityById(id)
        .unwrap()
        .then((community) => {
          const title =
            typeof community.title === 'string' ? community.title.trim() : '';
          const name = title || t('common.community');
          setResolvedCommunityNames((prev) => ({ ...prev, [id]: name }));
        })
        .catch(() => {
          setResolvedCommunityNames((prev) =>
            prev[id] ? prev : { ...prev, [id]: t('common.community') },
          );
        });
    });
  }, [
    items,
    resolvedCommunityNames,
    resolvedUserNames,
    t,
    triggerCommunityById,
    triggerUserDetails,
  ]);

  useEffect(() => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    setInitialLoading(true);
    setItems([]);
    setNextCursor(null);
    setHasMore(true);
    setListError(null);

    (async () => {
      try {
        const res =
          tab === 'all'
            ? await triggerAllRef.current({ limit: PAGE_SIZE }).unwrap()
            : await triggerUnreadRef.current({ limit: PAGE_SIZE }).unwrap();
        if (!isMountedRef.current || latestRequestIdRef.current !== requestId) {
          return;
        }
        setItems(res.items);
        setNextCursor(res.nextCursor);
        setHasMore(res.hasMore);
      } catch (error) {
        if (isMountedRef.current && latestRequestIdRef.current === requestId) {
          setListError(error);
        }
      } finally {
        if (isMountedRef.current && latestRequestIdRef.current === requestId) {
          setInitialLoading(false);
        }
      }
    })();
  }, [tab]);

  const refreshCurrentTab = useCallback(
    async (options?: { mode?: 'full' | 'soft' }) => {
      const mode = options?.mode ?? 'full';
      const currentTab = tab;
      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;

      if (mode === 'full') {
        setInitialLoading(true);
        setAppendLoading(false);
        setItems([]);
        setNextCursor(null);
        setHasMore(true);
        setListError(null);
      } else {
        setAppendLoading(false);
        setListError(null);
      }
      try {
        const res =
          currentTab === 'all'
            ? await triggerAllRef.current({ limit: PAGE_SIZE }).unwrap()
            : await triggerUnreadRef.current({ limit: PAGE_SIZE }).unwrap();
        if (!isMountedRef.current || latestRequestIdRef.current !== requestId) {
          return;
        }
        setItems(res.items);
        setNextCursor(res.nextCursor);
        setHasMore(res.hasMore);
      } catch (error) {
        if (isMountedRef.current && latestRequestIdRef.current === requestId) {
          setListError(error);
        }
      } finally {
        if (isMountedRef.current && latestRequestIdRef.current === requestId) {
          setInitialLoading(false);
        }
      }
    },
    [tab],
  );
  useRegisterPageContainerError({
    errors: listError ? [listError] : [],
    isLoading: initialLoading,
    hasData: items.length > 0,
    onRetry: refreshCurrentTab,
  });

  const { onRefresh } = usePullRefreshInvalidateTags(
    [
      {
        apiSlice: notificationsApi,
        tags: [
          NOTIFICATIONS_TAG,
          { type: UNREAD_COUNT_TAG, id: UNREAD_COUNT_TAG_ID },
        ],
      },
    ],
    {
      onAfterInvalidate: () => refreshCurrentTab({ mode: 'soft' }),
      hideDurationMs: 0,
    },
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || initialLoading || appendLoading) {
      return;
    }
    if (items.length === 0) {
      return;
    }
    const cursor = nextCursor ?? undefined;
    if (!cursor) {
      return;
    }
    setAppendLoading(true);
    try {
      const res =
        tab === 'all'
          ? await triggerAllRef.current({ limit: PAGE_SIZE, cursor }).unwrap()
          : await triggerUnreadRef
              .current({ limit: PAGE_SIZE, cursor })
              .unwrap();
      setItems((prev) => [...prev, ...res.items]);
      setNextCursor(res.nextCursor);
      setHasMore(res.hasMore);
    } finally {
      setAppendLoading(false);
    }
  }, [appendLoading, hasMore, initialLoading, items.length, nextCursor, tab]);

  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void loadMoreRef.current();
        }
      },
      { root: null, threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, tab]);

  const onVisibleUnread = useCallback((id: string) => {
    visibilityBufferRef.current.add(id);
  }, []);

  const handleReadAll = useCallback(async () => {
    setReadAllLoading(true);
    try {
      let cursor: string | undefined;
      let more = true;
      const ids: string[] = [];
      while (more) {
        const res = await triggerUnreadRef
          .current({
            limit: 50,
            cursor,
          })
          .unwrap();
        for (const it of res.items) {
          if (!it.readAt) {
            ids.push(it.id);
          }
        }
        more = res.hasMore;
        cursor = res.nextCursor ?? undefined;
        if (!more) {
          break;
        }
      }
      visibilityBufferRef.current.clear();
      if (ids.length > 0) {
        await markRead({ ids }).unwrap();
      }
      if (tab === 'unread') {
        setItems([]);
        setNextCursor(null);
        setHasMore(false);
      } else {
        const now = new Date().toISOString();
        setItems((prev) =>
          prev.map((n) =>
            ids.includes(n.id)
              ? { ...n, readAt: n.readAt ?? now, updatedAt: now }
              : n,
          ),
        );
      }
    } finally {
      setReadAllLoading(false);
    }
  }, [markRead, tab]);

  const description =
    unreadCount > 0
      ? t('inAppNotifications.subtitleUnread', { count: unreadCount })
      : t('inAppNotifications.subtitleAllCaughtUp');

  const showEmptyState = !initialLoading && items.length === 0;

  return (
    <PageContainer
      backButton
      title={t('inAppNotifications.pageTitle')}
      description={description}
      onRefresh={onRefresh}>
      <Box className={classes.toolbar}>
        <Box className={classes.toolbarTabs}>
          <AppTabsBase
            value={tab}
            onChange={(v) => setTab(v as 'all' | 'unread')}
            config={[
              { label: t('inAppNotifications.tabAll'), value: 'all' },
              {
                label: t('inAppNotifications.tabUnread'),
                value: 'unread',
                itemsCount: unreadCount > 0 ? unreadCount : undefined,
              },
            ]}
          />
        </Box>
        {isReadAllIconOnly ? (
          <IconButton
            className={classes.readAllIconButton}
            color="primary"
            aria-label={t('inAppNotifications.readAll')}
            title={t('inAppNotifications.readAll')}
            disabled={readAllLoading || unreadCount === 0}
            onClick={() => void handleReadAll()}>
            <DoneAllIcon />
          </IconButton>
        ) : (
          <Button
            className={classes.readAllButton}
            startIcon={<DoneAllIcon />}
            disabled={readAllLoading || unreadCount === 0}
            onClick={() => void handleReadAll()}>
            {t('inAppNotifications.readAll')}
          </Button>
        )}
      </Box>

      <Box className={classes.list}>
        {initialLoading
          ? Array.from({ length: INITIAL_SKELETON_COUNT }, (_, i) => (
              <NotificationRowSkeleton key={`sk-init-${i}`} />
            ))
          : null}
        {showEmptyState ? (
          <Typography variant="text-sm" className={classes.emptyStateText}>
            {tab === 'all'
              ? t('inAppNotifications.emptyAll')
              : t('inAppNotifications.emptyUnread')}
          </Typography>
        ) : null}
        {!initialLoading && items.length > 0
          ? items.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                currentUserId={me?.id}
                resolvedUserNames={resolvedUserNames}
                resolvedCommunityNames={resolvedCommunityNames}
                onVisibleUnread={onVisibleUnread}
              />
            ))
          : null}
        {appendLoading
          ? Array.from({ length: APPEND_SKELETON_COUNT }, (_, i) => (
              <NotificationRowSkeleton key={`sk-app-${i}`} compact />
            ))
          : null}
        <Box ref={sentinelRef} className={classes.sentinel} />
      </Box>
    </PageContainer>
  );
};

export default Notifications;
